import { requestCart, addCartItem, reconcileCart } from './cart-request.js';
import { revealDialog, dismissDialog, cancelDialogMotion } from './dialog-motion.js';
import { reconcileGiftCart } from 'cart-gift';

class CartPanel extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.controlDefaults = new WeakMap();
    const { signal } = this.controller;
    this.dialog = this.querySelector('dialog');
    if (!this.dialog?.showModal) return;
    this.toast = this.querySelector('[data-cart-confirmation]');
    this.addEventListener('click', this.onClick, { signal });
    this.addEventListener('change', this.onQuantity, { signal });
    this.addEventListener('submit', (event) => { if (this.busy || this.needsReview) event.preventDefault(); }, { signal });
    document.addEventListener('click', this.onOpen, { signal });
    document.addEventListener('cart:submit', this.onProductSubmit, { signal });
    document.addEventListener('cart:added', this.onAdded, { signal });
    document.addEventListener('cart:gift-pending', event => this.setBusy(event.detail.busy), { signal });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.toast.hidden) this.hideConfirmation(true);
    }, { signal });
    this.dialog.addEventListener('close', () => {
      if (this.dialog.open) return;
      cancelDialogMotion(this.dialog);
      if (!document.querySelector('dialog[open]') && this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
    }, { signal });
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    }, { signal });
    this.toast.addEventListener('pointerenter', () => clearTimeout(this.toastTimer), { signal });
    this.toast.addEventListener('pointerleave', () => this.scheduleConfirmation(), { signal });
    this.toast.addEventListener('focusin', () => clearTimeout(this.toastTimer), { signal });
    this.toast.addEventListener('focusout', () => this.scheduleConfirmation(), { signal });
    this.setAttribute('data-cart-ready', '');
    window.addEventListener('hashchange', this.openFromLocation, { signal });
    this.openFromLocation();
  }

  openFromLocation = () => {
    if (location.hash !== '#cart') return;
    history.replaceState(history.state, '', location.pathname + location.search);
    this.open(document.querySelector('[data-cart-section]'));
  };

  disconnectedCallback() {
    this.controller?.abort();
    clearTimeout(this.toastTimer);
    if (this.dialog) cancelDialogMotion(this.dialog);
    this.dialog?.close();
  }

  onOpen = (event) => {
    const link = event.target.closest('[data-cart-section], [data-cart-open]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    this.open(link);
  };

  open(trigger, refresh = true) {
    this.trigger = this.toast.contains(trigger) ? document.querySelector('[data-cart-section]') : trigger;
    this.hideConfirmation();
    document.dispatchEvent(new CustomEvent('cart:opening'));
    if (!this.dialog.open) {
      this.dialog.showModal();
      revealDialog(this.dialog);
    } else if (this.dialog.hasAttribute('data-dialog-closing')) revealDialog(this.dialog);
    this.dialog.querySelector('h2').focus({ preventScroll: true });
    if (refresh) this.refresh(true);
  }

  close() {
    if (this.dialog?.open) dismissDialog(this.dialog, () => this.dialog.close());
  }

  onClick = (event) => {
    if (event.target.closest('[data-cart-close]')) this.close();
    if (event.target.closest('[data-confirmation-close]')) this.hideConfirmation(true);
    if (event.target.closest('[data-cart-refresh]')) this.refresh(true);
    if (event.target === this.dialog) {
      const rect = this.dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
    }
    const control = event.target.closest('[data-cart-step], [data-cart-remove]');
    if (!control) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    if (this.busy || this.needsReview) return;
    const line = control.closest('[data-cart-line]');
    const input = line.querySelector('[data-cart-quantity]');
    if (control.hasAttribute('data-cart-remove')) this.change(line, 0, 'remove');
    else {
      const previous = input.value;
      if (control.dataset.cartStep === '1') input.stepUp();
      else input.stepDown();
      if (input.value !== previous) this.change(line, Number(input.value), control.dataset.cartStep);
    }
  };

  onQuantity = (event) => {
    const input = event.target.closest('[data-cart-quantity]');
    if (!input || this.busy || this.needsReview || !input.reportValidity()) return;
    this.change(input.closest('[data-cart-line]'), Number(input.value), 'quantity');
  };

  setBusy(busy) {
    this.busy = busy;
    this.toggleAttribute('data-busy', busy);
    const content = this.querySelector('[data-cart-content]');
    content.setAttribute('aria-busy', String(busy));
    content.querySelectorAll('button,input,select').forEach((control) => {
      if (!this.controlDefaults.has(control)) this.controlDefaults.set(control, control.disabled);
      control.disabled = busy || Boolean(this.needsReview) || this.controlDefaults.get(control);
    });
  }

  notice(message = '') {
    const notice = this.querySelector('[data-cart-notice]');
    notice.hidden = !message;
    notice.querySelector('[data-cart-message]').textContent = message;
  }

  async refresh(review = false) {
    if (this.busy) return;
    this.setBusy(true);
    this.notice();
    try {
      const url = new URL(this.dataset.renderUrl, location.origin);
      url.searchParams.set('sections', 'cart-panel');
      const result = await requestCart(url.href);
      if (!this.isConnected) return;
      if (!this.render(result['cart-panel'])) throw new Error('Missing cart section');
      this.needsReview = false;
      if (review) reconcileCart();
      return true;
    } catch {
      this.needsReview = true;
      if (this.isConnected) this.notice(this.dataset.errorLabel);
      return false;
    } finally {
      if (this.isConnected) this.setBusy(false);
    }
  }

  async change(line, quantity, control) {
    if (!Number.isInteger(quantity) || quantity < 0) return;
    const key = line.dataset.lineKey;
    const index = [...line.parentElement.children].indexOf(line);
    this.setBusy(true);
    this.notice();
    try {
      let result = await requestCart(this.dataset.changeUrl + '.js', {
        method: 'POST', body: JSON.stringify({ id: key, quantity, sections: ['cart-panel'], sections_url: location.pathname }),
      });
      result = await reconcileGiftCart(result, { updateUrl: this.dataset.changeUrl.replace(/\/change$/, '/update'), sections: ['cart-panel'], sectionsUrl: location.pathname });
      if (!this.isConnected) return;
      if (!this.render(result.sections?.['cart-panel'])) {
        this.setBusy(false);
        if (!await this.refresh()) return;
      }
      this.querySelector('[data-cart-status]').textContent = this.dataset.updatedLabel;
      this.setBusy(false);
      const lines = [...this.querySelectorAll('[data-cart-line]')];
      const target = lines.find((item) => item.dataset.lineKey === key) || lines[Math.min(index, lines.length - 1)];
      const selector = control === 'quantity' ? '[data-cart-quantity]' : control === 'remove' ? '[data-cart-remove]' : `[data-cart-step="${control}"]`;
      const requestedControl = target?.querySelector(selector);
      const focusTarget = requestedControl?.disabled ? target.querySelector('[data-cart-quantity]') : requestedControl;
      (focusTarget || this.dialog.querySelector('h2')).focus({ preventScroll: true });
    } catch (error) {
      this.needsReview = true;
      const input = line.querySelector('[data-cart-quantity]');
      if (input) input.value = input.defaultValue;
      if (this.isConnected) this.notice([error.message, error.uncertain ? this.dataset.uncertainLabel : this.dataset.errorLabel].filter(Boolean).join(' '));
    } finally {
      if (this.isConnected) this.setBusy(false);
    }
  }

  render(html) {
    if (typeof html !== 'string') return false;
    // Only Shopify's own section response crosses this HTML boundary.
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const source = parsed.querySelector('[data-cart-content]');
    if (!source || !/^\d+$/.test(source.dataset.count || '')) return false;
    const target = this.querySelector('[data-cart-content]');
    target.replaceChildren(...source.childNodes);
    target.dataset.count = source.dataset.count;
    target.dataset.countLabel = source.dataset.countLabel;
    document.querySelectorAll('[data-cart-section] [data-cart-count]').forEach((count) => { count.textContent = source.dataset.count; });
    document.querySelectorAll('[data-cart-section] [data-cart-label]').forEach((label) => { label.textContent = source.dataset.countLabel; });
    document.dispatchEvent(new CustomEvent('cart:updated'));
    return true;
  }

  onAdded = async (event) => {
    const { result, item, openCart, trigger } = event.detail;
    if (!this.render(result.sections?.['cart-panel'])) await this.refresh();
    if (!this.isConnected) return;
    if (openCart && !this.dialog.open) this.open(trigger, false);
    if (this.dialog.open) this.querySelector('[data-cart-status]').textContent = this.dataset.addedLabel;
    else this.confirm(item);
  };

  onProductSubmit = (event) => {
    if (event.defaultPrevented) return;
    const { form, submitter } = event.detail;
    if (form.method !== 'post') return;
    event.preventDefault();
    if (form.hasAttribute('data-cart-pending')) return;
    this.submitProduct(form, submitter);
  };

  async submitProduct(form, submitter) {
    const feedback = form.closest('product-detail')?.querySelector('[data-product-feedback]');
    if (feedback) feedback.replaceChildren();
    const pending = addCartItem(form);
    form.setAttribute('data-cart-pending', '');
    form.dispatchEvent(new CustomEvent('cart:pending', { bubbles: true }));
    submitter.disabled = true;
    submitter.setAttribute('aria-busy', 'true');
    try {
      const result = await pending;
      const item = result.items?.[0] || result;
      const detail = { result, item };
      form.dispatchEvent(new CustomEvent('cart:success', { bubbles: true, detail }));
      document.dispatchEvent(new CustomEvent('cart:added', { detail }));
    } catch (error) {
      if (feedback?.isConnected) {
        feedback.textContent = [error.message, error.uncertain ? this.dataset.uncertainLabel : this.dataset.errorLabel].filter(Boolean).join(' ');
        if (error.uncertain) {
          const link = document.createElement('a');
          link.href = this.dataset.cartUrl;
          link.dataset.cartOpen = '';
          link.textContent = this.querySelector('[data-cart-open]').textContent;
          feedback.append(' ', link);
        }
      }
    } finally {
      form.removeAttribute('data-cart-pending');
      submitter.removeAttribute('aria-busy');
      submitter.disabled = false;
      form.dispatchEvent(new CustomEvent('cart:settled', { bubbles: true }));
    }
  }

  confirm(item) {
    const title = item.product_title || item.title;
    this.querySelector('[data-confirmation-title]').textContent = title;
    const line = [...this.querySelectorAll('[data-cart-line]')].find((row) => row.dataset.lineKey === item.key);
    const price = line?.querySelector('[data-line-unit-price]')?.textContent || '';
    const variant = item.variant_title === 'Default Title' ? '' : item.variant_title || '';
    this.querySelector('[data-confirmation-detail]').textContent = [variant, price].filter(Boolean).join(' · ');
    const media = this.querySelector('[data-confirmation-media]');
    media.replaceChildren();
    const src = item.featured_image?.url || item.image;
    if (src) {
      try {
        const url = new URL(src, location.origin);
        if (url.protocol === 'https:' || url.origin === location.origin) {
          url.searchParams.set('width', '112');
          const image = document.createElement('img');
          image.src = url.href;
          image.width = 56;
          image.height = 64;
          image.alt = '';
          media.append(image);
        }
      } catch { /* Missing media does not affect confirmation. */ }
    }
    this.toast.hidden = false;
    this.querySelector('[data-confirmation-status]').textContent = `${this.dataset.addedLabel}: ${title}`;
    this.scheduleConfirmation();
  }

  scheduleConfirmation() {
    clearTimeout(this.toastTimer);
    if (!this.toast.matches(':hover') && !this.toast.contains(document.activeElement)) {
      this.toastTimer = setTimeout(() => this.hideConfirmation(), 6500);
    }
  }

  hideConfirmation(restoreFocus = false) {
    clearTimeout(this.toastTimer);
    if (restoreFocus && this.toast.contains(document.activeElement)) document.querySelector('[data-cart-section]')?.focus();
    this.toast.hidden = true;
  }
}

if (!customElements.get('cart-panel')) customElements.define('cart-panel', CartPanel);
